import { createQueryHook } from '@fe-toolkit/react-query-hooks-toolkit'
import { useQueryClient } from '@tanstack/react-query'
import getConfig from 'next/config'
import { useEffect } from 'react'

import createApiWithConfig from '@/api'
// import { useError } from '@/context'

const { publicRuntimeConfig } = getConfig()

/**
 * Custom hook for accessing various API endpoints.
 * Allows runtime configuration of the API client.
 **/
export const useApiHook = () => {
  const queryClient = useQueryClient()
  // const { setErrorCode } = useError()

  // create api client
  const apiClient = createApiWithConfig(publicRuntimeConfig, {
    onResponseValidationError: ({ validateResponseGlobal, validateResponseLocal }) => {
      if (validateResponseLocal ?? validateResponseGlobal) {
        // TODO:re instate this once we are not getting zod schema errors.
        // setErrorCode(500)
      }
    },
  })

  const { api: publicApi } = apiClient.publicApi

  useEffect(() => {
    queryClient.setDefaultOptions({
      queries: {
        retry: false,
        enabled: true, // set to false in _app.tsx to prevent initial fetch
      },
    })
  }, [queryClient])

  // get Plan by Member Id
  const useGetProfileOverview = createQueryHook(publicApi.getProfileOverview, { select: ({ data }) => data })
  const useGetOrderDetails = createQueryHook(publicApi.getOrderDetails)

  const useUprnProductsEndpoint = createQueryHook(publicApi.getProductsByUprn, { select: ({ data }) => data })
  const useUpdateOrderActiveAppointment = createQueryHook(publicApi.updateOrderActiveAppointments)

  const useGetCpeOrder = createQueryHook(publicApi.getCpeOrder)

  const useGetECommerceOrderSchedule = createQueryHook(publicApi.getECommerceOrderSchedule, {
    select: ({ data }) => data,
  })

  const useGetPlan = createQueryHook(publicApi.getPlanById, {
    select: ({ data }) => data,
    refetchOnWindowFocus: false,
  })
  const useGetPlanAmendmentDetails = createQueryHook(publicApi.getPlanAmendmentDetails, {
    select: ({ data }) => data,
    refetchOnWindowFocus: false,
  })
  const useGetPlanBuyoutDetails = createQueryHook(publicApi.getPlanBuyoutDetails, {
    select: ({ data }) => data,
    refetchOnWindowFocus: false,
  })
  const useGetEvidenceSession = createQueryHook(publicApi.getEvidenceSession, {
    select: ({ data }) => data,
    refetchOnWindowFocus: false,
  })
  const useGetChangePlanPreview = createQueryHook(publicApi.getChangePlanPreview, {
    select: ({ data }) => data,
    refetchOnWindowFocus: false,
  })

  const useGetOrchestratedPayments = createQueryHook(publicApi.getOrchestratedPayments, {
    select: ({ data }) => data ?? [],
    refetchOnWindowFocus: false,
  })

  const useGetPaymentDetailsByCallerReferenceId = createQueryHook(publicApi.getPaymentDetailsByCallerReferenceId, {
    select: ({ data }) => data ?? [],
    refetchOnWindowFocus: false,
  })

  const useGetMemberCardDetails = createQueryHook(publicApi.getMemberCardDetails, {
    select: ({ data }) => data ?? [],
    refetchOnWindowFocus: false,
  })

  const useGetPlanAmendmentProducts = createQueryHook(publicApi.getPlanAmendmentProducts, {
    select: ({ data }) => data ?? { products: {} },
    refetchOnWindowFocus: false,
  })

  return {
    apiClient,
    queryClient,
    useGetProfileOverview,
    useGetOrderDetails,
    useUprnProductsEndpoint,
    useUpdateOrderActiveAppointment,
    useGetCpeOrder,
    useGetECommerceOrderSchedule,
    useGetPlan,
    useGetPlanAmendmentDetails,
    useGetPlanBuyoutDetails,
    useGetEvidenceSession,
    useGetChangePlanPreview,
    useGetOrchestratedPayments,
    useGetPaymentDetailsByCallerReferenceId,
    useGetMemberCardDetails,
    useGetPlanAmendmentProducts,
  }
}

export default useApiHook
